import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { axiosService } from 'shared/helpers/axios';
import { useUserContext } from 'shared/contexts/UserContext';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { apiErrorMessage } from 'shared/helpers/apiError';

const NotificationContext = createContext();

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider');
  return ctx;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { isAuthenticated } = useUserContext();
  const { showSnackbar } = useSnackbar();
  const wasAuthenticatedRef = useRef(false);

  const updateUnreadCount = useCallback((list) => {
    const cnt = (list || []).filter(n => !n.read && !n.is_read).length;
    setUnreadCount(cnt);
  }, []);

  const fetchNotifications = useCallback(async () => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      return;
    }
    
    try {
      const res = await axiosService.get('/interact/notifications/');
      const data = res.data?.results ?? res.data ?? [];
      // Filter out message notifications
      const filtered = data.filter(n => n.ntype !== 'message');
      const normalized = filtered.map(n => ({ ...n, read: !!n.is_read }));
      setNotifications(normalized);
      updateUnreadCount(normalized);
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    }
  }, [isAuthenticated, updateUnreadCount]);

  useEffect(() => {
    // Only start polling if authenticated
    if (!isAuthenticated) {
      return;
    }
    
    fetchNotifications();
    const i = setInterval(fetchNotifications, 30000); // 30s (reduced frequency)
    // and at once when something answered a notification elsewhere (a follow request on the feed card…)
    window.addEventListener('otable:notifications-changed', fetchNotifications);
    return () => {
      clearInterval(i);
      window.removeEventListener('otable:notifications-changed', fetchNotifications);
    };
  }, [isAuthenticated, fetchNotifications]);

  // Re-fetch immediately when the user logs in (transitions from unauthenticated to authenticated)
  useEffect(() => {
    if (isAuthenticated && !wasAuthenticatedRef.current) {
      fetchNotifications();
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axiosService.post(`/interact/notifications/${notificationId}/mark-read/`);
    } catch (e) {
      console.warn('Failed to mark-read on server (continuing optimistically):', e);
    }
    setNotifications(prev => {
      const updated = prev.map(n => n.id === notificationId ? { ...n, read: true, is_read: true } : n);
      updateUnreadCount(updated);
      return updated;
    });
  };

  const markAllAsRead = async () => {
    try {
      await axiosService.post('/interact/notifications/mark-all-read/');
    } catch (e) {
      console.warn('Failed to mark-all-read on server (continuing optimistically):', e);
    }
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true, is_read: true }));
      updateUnreadCount(updated);
      return updated;
    });
  };

  const deleteNotification = (notificationId) => {
    setNotifications(prev => {
      const updated = prev.filter(n => n.id !== notificationId);
      updateUnreadCount(updated);
      return updated;
    });
  };

  // Centralized action executor; expects { action: string, meta?: { id?: string }, ... }
  const handleNotificationAction = async (notificationId, action) => {
    // Mark as read optimistically on any action
    await markAsRead(notificationId);

    const id = action?.meta?.id || action?.invitationId || action?.suggestionId;
    try {
      switch (action?.action) {
        case 'accept_invitation':
          if (id) {
            try {
              await axiosService.post(`/interact/consortium-invitations/${id}/accept/`);
            } catch (error) {
              if (error.response?.status === 400) {
                const errorData = error.response.data;
                if (errorData.error?.includes('primary organization')) {
                  throw new Error('Please set your primary organization in your profile before accepting consortium invitations.');
                }
                throw new Error(errorData.error || errorData.detail || 'Failed to accept invitation');
              }
              throw error;
            }
          }
          break;
        case 'cancel_acceptance':
          if (id) await axiosService.post(`/interact/consortium-invitations/${id}/cancel/`);
          break;
        case 'decline_invitation':
          if (id) await axiosService.post(`/interact/consortium-invitations/${id}/reject/`);
          break;
        case 'ignore_invitation':
          await axiosService.delete(`/interact/notifications/${notificationId}/`).catch(() => {});
          break;

        case 'view_suggestion':
          // navigation handled by UI; we still record a read
          if (id) await axiosService.post(`/interact/suggestions/${id}/mark-read/`).catch(() => {});
          break;
        case 'save_suggestion':
          if (id) await axiosService.post(`/interact/suggestions/${id}/accept/`);
          break;

        case 'dismiss_suggestion':
          if (id) await axiosService.post(`/interact/suggestions/${id}/reject/`);
          break;

        case 'accept_join_request':
          if (id) await axiosService.patch(`/interact/join-requests/${id}/`, { status: 'accepted' });
          break;

        case 'reject_join_request':
          if (id) await axiosService.patch(`/interact/join-requests/${id}/`, { status: 'rejected' });
          break;

        case 'accept_proposal_reinvite': {
          const roleId = action?.meta?.role_id;
          if (roleId) await axiosService.post(`/consortium/member-roles/${roleId}/rejoin/`);
          await axiosService.delete(`/interact/notifications/${notificationId}/`).catch(() => {});
          break;
        }

        case 'block_proposal_invite': {
          const roleId = action?.meta?.role_id;
          if (roleId) await axiosService.post(`/consortium/member-roles/${roleId}/block-invite/`);
          await axiosService.delete(`/interact/notifications/${notificationId}/`).catch(() => {});
          break;
        }

        default:
          // Unknown, ignore
          break;
      }
    } catch (e) {
      console.error('Failed to execute action:', action, e);
      // Display user-friendly error message via snackbar
      const errorMessage = apiErrorMessage(e, 'An error occurred while processing your request');
      showSnackbar(errorMessage);
      // Re-throw to allow component-level handling if needed
      throw e;
    } finally {
      // Always refresh to reflect latest server state (accepted/declined notifications, etc.)
      fetchNotifications();
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        handleNotificationAction,
        refresh: fetchNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
